import {
  useEffect,
  useState
} from "react";
import {
  User,
  UserRound,
  ShieldCheck
} from "lucide-react";
import {
  useStaffAuth
} from "../context/StaffAuthContext";
import {
  updateMyProfile
} from "../api/staffPortalApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Skeleton,
  Badge
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const MyProfile = () => {
  const { staff, refreshStaff } = useStaffAuth();

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address: "",
    additionalInfo: {
      dateOfBirth: "",
      bloodGroup: "",
      emergencyContact: {
        name: "",
        relation: "",
        mobile: "",
      },
    },
  });

  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Populate form from staff context
  useEffect(() => {
    if (!staff) return;

    setForm({
      name: staff.name || "",
      mobile: staff.mobile || "",
      address: staff.address || "",
      additionalInfo: {
        dateOfBirth: staff.additionalInfo?.dateOfBirth
          ? new Date(staff.additionalInfo.dateOfBirth).toISOString().split("T")[0]
          : "",
        bloodGroup: staff.additionalInfo?.bloodGroup || "",
        emergencyContact: {
          name: staff.additionalInfo?.emergencyContact?.name || "",
          relation: staff.additionalInfo?.emergencyContact?.relation || "",
          mobile: staff.additionalInfo?.emergencyContact?.mobile || "",
        },
      },
    });
  }, [staff]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdditionalChange = (e) => {
    setForm({
      ...form,
      additionalInfo: {
        ...form.additionalInfo,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleEmergencyChange = (e) => {
    setForm({
      ...form,
      additionalInfo: {
        ...form.additionalInfo,
        emergencyContact: {
          ...form.additionalInfo.emergencyContact,
          [e.target.name]: e.target.value,
        },
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      await updateMyProfile(form);
      await refreshStaff();

      toast.success("Profile updated successfully.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (!staff) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-4">
        <Skeleton height={100} width="100%" />
        <Skeleton height={400} width="100%" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-5 antialiased">
      {/* Profile Header Banner */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
        <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border border-neutral-200/80">
            {staff.gender === "Female" ? (
              <UserRound className="w-8 h-8" />
            ) : (
              <User className="w-8 h-8" />
            )}
          </div>
          <div className="space-y-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-center sm:justify-start">
              <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{staff.name}</h1>
              <Badge variant="secondary" className="w-fit mx-auto sm:mx-0 text-[10px] font-medium">
                {staff?.roleId?.roleName || "Staff Member"}
              </Badge>
            </div>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 font-mono">Staff Code: {staff.staffCode}</p>
          </div>
        </CardContent>
      </Card>

      {/* Main Form Container */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 sm:px-6 py-4">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
            Personal Profile Settings
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4 sm:p-6 space-y-6">
          {/* Notifications */}
          
          

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section: Basic Info */}
            <div className="space-y-3">
              <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider block border-b border-neutral-100 dark:border-neutral-800 pb-1.5">
                Basic Information
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Full Name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
                <Input
                  label="Mobile Number"
                  type="text"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                  Residential Address
                </label>
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  rows="3"
                  className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Section: Additional Info */}
            <div className="space-y-3">
              <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider block border-b border-neutral-100 dark:border-neutral-800 pb-1.5">
                Additional Information
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Date of Birth"
                  type="date"
                  name="dateOfBirth"
                  value={form.additionalInfo.dateOfBirth}
                  onChange={handleAdditionalChange}
                />

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                    Blood Group
                  </label>
                  <select
                    name="bloodGroup"
                    value={form.additionalInfo.bloodGroup}
                    onChange={handleAdditionalChange}
                    className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Select Blood Group</option>
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Section: Emergency Contact */}
            <div className="space-y-3">
              <span className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider block border-b border-neutral-100 dark:border-neutral-800 pb-1.5">
                Emergency Contact Details
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="Contact Name"
                  type="text"
                  name="name"
                  value={form.additionalInfo.emergencyContact.name}
                  onChange={handleEmergencyChange}
                  placeholder="e.g. Ramesh Kumar"
                />
                <Input
                  label="Relation"
                  type="text"
                  name="relation"
                  value={form.additionalInfo.emergencyContact.relation}
                  onChange={handleEmergencyChange}
                  placeholder="e.g. Spouse, Parent"
                />
                <Input
                  label="Contact Mobile"
                  type="text"
                  name="mobile"
                  value={form.additionalInfo.emergencyContact.mobile}
                  onChange={handleEmergencyChange}
                  placeholder="e.g. 9876543210"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                isLoading={loading}
                className="w-full sm:w-auto px-6 text-xs font-semibold py-2.5"
              >
                Save Profile
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyProfile;